import { Immutable } from "immer";
import React from "react";

import { templates } from "@reactivated";
import type {
    LandingCustomerReviewsBlock as LandingCustomerReviewsBlockValue,
    LandingProductInspirationBlock as LandingProductInspirationBlockValue,
    LandingPromoProductBlock as LandingPromoProductBlockValue,
    ThreeByThreeImageBlock as ThreeByThreeImageBlockValue,
} from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { LandingCustomerReviewsBlock } from "../../blocks/LandingCustomerReviewsBlock";
import { LandingProductInspirationBlock } from "../../blocks/LandingProductInspirationBlock";
import { LandingPromoProductBlock } from "../../blocks/LandingPromoProductBlock";
import { ThreeByThreeImageBlock } from "../../blocks/ThreeByThreeImageBlock";
import { toolboxComponentsMap } from "../../blocks/toolboxComponentsMap";

/**
 * toppers LandingPage ``landing_page_props`` BOTTOM body assembly.
 *
 * FAITHFUL port of the SECOND ``{% for block in self.landing_page_props %}`` loop
 * in ``cms/landing_page.html``, which sits BELOW the promo-bar and renders nine
 * block types via ``{% if %}/{% elif %}`` (NO ``else``):
 *   customer_reviews_block, product_callout, promo_product_block,
 *   product_inspiration_block, three_by_three_image_block, product_compare_grid,
 *   banner, promo_conditional_block, product_selection_grid.
 *
 * SILENT-DROP (load-bearing): ``landing_page_props`` carries ~18 block types,
 * but this BOTTOM loop matched only the nine above; every other type fell
 * through with no ``else`` and rendered nothing. thelabui ``StreamField`` THROWS
 * on an unmapped block type, so we PRE-FILTER the value array to this island's
 * nine keys (order-preserving) and pass a map covering exactly those keys --
 * reproducing the drop AND field-relative order. The two TOP-loop types
 * (product_description_block / product_value_prop_block) are handled by the
 * disjoint LandingPageBodyTopTemplate; the two key sets do not overlap.
 *
 * The five SHARED keys (banner, product_callout, product_compare_grid,
 * product_selection_grid, promo_conditional_block) reuse the EXACT toolbox
 * renderers -- picked off ``toolboxComponentsMap`` (NOT spread) so only this
 * island's keys are present. This preserves the recursive promo_conditional_block
 * dispatch and the SSR-envelope grids.
 *
 * The five LANDING-specific keys use the LANDING variants (NOT the
 * shared CustomerReviewsBlock):
 *   - customer_reviews_block -> LandingCustomerReviewsBlock;
 *   - promo_product_block    -> LandingPromoProductBlock;
 *   - product_inspiration_block -> LandingProductInspirationBlock;
 *   - three_by_three_image_block -> wrapped in the page-context anchor (below).
 *
 * three_by_three wrapper (load-bearing): the ``three_by_three`` branch
 * wrapped the block's ``{{ block }}`` render in a ``.landing-page-reviews``
 * anchor (href = the page hero PDP url + ``#reviews``) plus a SEE MORE button.
 * The PDP url is PAGE context the block cannot resolve, so the wrapper lives
 * here. ThreeByThreeImageBlock is anchor-free (article/div/heading/SVG only),
 * so the wrapper anchor introduces no nested ``<a>`` of its own.
 *
 * SEE MORE de-nest (deliberate divergence from the original markup): the
 * markup was a NESTED anchor --
 *   ``<a class="landing-page-reviews__button" href=PDP#reviews>``
 *     ``<a class="button button--ming">SEE MORE</a></a>``
 * -- which is invalid HTML. As STATIC Django HTML the browser's parser ran the
 * adoption-agency algorithm and de-nested it on load, so the SEE MORE was
 * never actually a working link (the de-nested ``button--ming`` anchor lost its
 * href). Inside this SSR'd + client-hydrated React island, React 19 would flag a
 * hydration mismatch (server emits literal nested ``<a><a>``; the browser re-parents
 * it), so we cannot keep it. We emit a SINGLE anchor with the visual button as an
 * inner ``<span>``: the ``.landing-page-reviews__button`` wrapper is a pure
 * centering wrapper (``text-align: center; margin-top: 49px`` only -- no
 * background/padding/border and no descendant combinator requiring an anchor
 * child), and ``.button.button--ming`` is element-agnostic, so a ``<span>`` is
 * visually identical to the de-nested button. Net: valid HTML, no
 * hydration mismatch, both classnames preserved for CSS, and SEE MORE now
 * actually links to ``PDP#reviews``.
 *
 * Because ThreeByThreeImageBlock returns a Fragment, its ``__container-left`` /
 * ``__container-right`` floats are DIRECT children of the ``.landing-page-reviews``
 * anchor, so the base ``.landing-page-reviews { @include clearfix; }`` contains
 * them (the ``> [data-include-react]`` clearfix is now obsolete and was
 * removed from _landing-page.scss).
 */

const {
    banner,
    product_callout,
    product_compare_grid,
    product_selection_grid,
    promo_conditional_block,
} = toolboxComponentsMap;

// The full ``landing_page_props`` union element type, and the BOTTOM-island
// subset narrowed off it by discriminant. thelabui's ``StreamField`` types
// ``components`` against the value's element union; a plain ``.filter()`` does
// NOT narrow that union, so the typed predicate below is what lets the nine-key
// partial map type-check (and the runtime throw on an unmapped type enforces the
// silent-drop at render time).
type LandingBlock = NonNullable<
    Immutable<{
        page: Pick<templates.LandingPageTemplate["page"], "landing_page_props">;
        hero_product_pdp_url: string | null;
    }>["page"]["landing_page_props"]
>["value"][number];
type BottomBlock = Extract<
    LandingBlock,
    {
        type:
            | "customer_reviews_block"
            | "product_callout"
            | "promo_product_block"
            | "product_inspiration_block"
            | "three_by_three_image_block"
            | "product_compare_grid"
            | "banner"
            | "promo_conditional_block"
            | "product_selection_grid";
    }
>;

const BOTTOM_KEYS = new Set<BottomBlock["type"]>([
    "customer_reviews_block",
    "product_callout",
    "promo_product_block",
    "product_inspiration_block",
    "three_by_three_image_block",
    "product_compare_grid",
    "banner",
    "promo_conditional_block",
    "product_selection_grid",
]);

const isBottomBlock = (block: LandingBlock): block is BottomBlock =>
    (BOTTOM_KEYS as Set<string>).has(block.type);

// The LandingPage bottom-body dispatch map. Built via this factory so the
// block-screenshot styleguide (StyleguideBlockTemplate) can route
// `three_by_three_image_block` through the identical production renderer with a
// zeroed `pdpUrl`. The only page-derived input is the review anchor's `href`
// (``${pdpUrl}#reviews``); with pdpUrl="" it degrades to `#reviews`, exactly as
// a real landing page with no hero PDP renders. The block's own content is
// fully carried by the serialized value, so this is render-faithful.
export const buildLandingBottomComponentsMap = (pdpUrl: string) =>
    ({
        // --- shared toolbox renderers (picked, not spread) ---
        banner,
        product_callout,
        product_compare_grid,
        product_selection_grid,
        promo_conditional_block,
        // --- landing-specific variants ---
        customer_reviews_block: (block: {
            value: Immutable<LandingCustomerReviewsBlockValue>;
        }) => <LandingCustomerReviewsBlock value={block.value} />,
        promo_product_block: (block: {
            value: Immutable<LandingPromoProductBlockValue>;
        }) => <LandingPromoProductBlock value={block.value} />,
        product_inspiration_block: (block: {
            value: Immutable<LandingProductInspirationBlockValue>;
        }) => <LandingProductInspirationBlock value={block.value} />,
        // three_by_three -> wrapped in the page-context anchor.
        three_by_three_image_block: (block: {
            value: Immutable<ThreeByThreeImageBlockValue>;
        }) => (
            <div className="l-full-width u-margin-bottom--xl">
                <a href={`${pdpUrl}#reviews`} className="landing-page-reviews">
                    <ThreeByThreeImageBlock value={block.value} />
                </a>
                <a
                    className="landing-page-reviews__button"
                    href={`${pdpUrl}#reviews`}
                >
                    <span className="button button--ming">SEE MORE</span>
                </a>
            </div>
        ),
    }) as const;

export const LandingPageBodyBottom = (
    props: Immutable<{
        page: Pick<templates.LandingPageTemplate["page"], "landing_page_props">;
        hero_product_pdp_url: string | null;
    }>,
): React.JSX.Element => {
    const bottomComponentsMap = buildLandingBottomComponentsMap(
        props.hero_product_pdp_url ?? "",
    );

    const value = (props.page.landing_page_props?.value ?? []).filter(
        isBottomBlock,
    );
    return (
        <StreamField
            tagName={React.Fragment}
            value={value}
            components={bottomComponentsMap}
        />
    );
};
